import React, { useEffect, useMemo, useState } from "react";

export type PaginationBarProps = {
  page: number;
  pageSize: number;
  totalPages?: number;
  pageSizeOptions: number[];
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  onGoToPage: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  disablePrev: boolean;
  disableNext: boolean;
  disableLast: boolean;
};

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  pageSize,
  totalPages,
  pageSizeOptions,
  onFirst,
  onPrev,
  onNext,
  onLast,
  onGoToPage,
  onPageSizeChange,
  disablePrev,
  disableNext,
  disableLast,
}) => {
  const [gotoValue, setGotoValue] = useState<string>(() => String(page));
  const sanitizedTarget = useMemo(() => {
    const trimmed = gotoValue.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    const floored = Math.floor(parsed);
    return floored > 0 ? floored : 1;
  }, [gotoValue]);

  useEffect(() => {
    setGotoValue(String(page));
  }, [page]);

  const handleGoToPage = () => {
    if (sanitizedTarget === null) return;
    const clamped =
      totalPages !== undefined
        ? Math.min(Math.max(1, sanitizedTarget), Math.max(1, totalPages))
        : Math.max(1, sanitizedTarget);
    setGotoValue(String(clamped));
    onGoToPage(clamped);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
          onClick={onFirst}
          disabled={disablePrev}
        >
          Trang đầu
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
          onClick={onPrev}
          disabled={disablePrev}
        >
          Trang trước
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Trang</span>
          <input
            type="number"
            min={1}
            value={gotoValue}
            onChange={(e) => setGotoValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleGoToPage();
              }
            }}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
            aria-label="Go to page"
          />
          <span className="text-sm text-gray-500">
            {totalPages ? `trên ${totalPages}` : "trên ?"}
          </span>
          <button
            type="button"
            className="rounded border border-blue-500 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent"
            onClick={handleGoToPage}
            disabled={sanitizedTarget === null}
          >
            Đi
          </button>
        </div>
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
          onClick={onNext}
          disabled={disableNext}
        >
          Trang tiếp
        </button>
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
          onClick={onLast}
          disabled={disableLast}
        >
          Trang cuối
        </button>
        <div className="ml-2 flex items-center gap-2">
          <span className="text-sm text-gray-700">Số dòng/trang:</span>
          <select
            className="rounded border border-gray-300 px-2 py-1 text-sm"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="flex items-center gap-2 rounded bg-gray-50 px-3 py-1 text-gray-700">
          <span className="text-xs uppercase tracking-wide text-gray-500">
            Trang
          </span>
          <span className="font-semibold text-gray-800">{page}</span>
          <span className="text-xs text-gray-500">
            {totalPages ? `trên ${totalPages}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <span className="text-xs uppercase tracking-wide text-gray-500">
            Số dòng/trang
          </span>
          <span className="font-semibold text-gray-800">{pageSize}</span>
        </div>
      </div>
    </div>
  );
};

export default PaginationBar;
